import { useState, useEffect } from 'react';
import NicknameBar from '../components/NicknameBar';
import TeamBadge from '../components/TeamBadge';
import OrnamentSeparator from '../components/OrnamentSeparator';
import { useAudienceIdentity } from '../context/AudienceIdentityContext';
import { TEAM_SHORT_NAMES, getTeamButtonClass } from '../components/teamTheme';

interface SheetInfo {
  id: string;
  name: string;
  imageUrl?: string;
}

interface VotingState {
  isActive: boolean;
  voteId: string | null;
  canVote: boolean;
  hasVoted: boolean;
  votedForSheetId: string | null;
}

interface StandbyScreenProps {
  onViewCharacter?: (characterId: string) => void;
  waitingForResults?: boolean;
  votingState?: VotingState;
  waitingForTeam?: string | null; // Team currently voting (for SEQUENTIAL mode)
  onVoteComplete?: (sheetId: string) => void;
}

/**
 * Standby Screen
 *
 * Schermata di attesa con Logo Dungeon World e pulsanti per consultare
 * le schede dei personaggi (2/3/4 in base al numero di giocatori).
 */
function StandbyScreen({ onViewCharacter, waitingForResults = false, votingState, waitingForTeam, onVoteComplete }: StandbyScreenProps) {
  const [sheets, setSheets] = useState<SheetInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [votingFor, setVotingFor] = useState<string | null>(null);
  const identity = useAudienceIdentity();

  // Get user's team from context (falls back to localStorage)
  const userTeam = (identity.teamColor || '').toUpperCase();
  const isVotingActive = votingState?.isActive && votingState?.canVote;
  const hasVoted = votingState?.hasVoted || false;
  const votedForSheetId = votingState?.votedForSheetId || null;

  useEffect(() => {
    loadSheets();
  }, []);

  // Reset local voting state when voting ends
  useEffect(() => {
    if (!votingState?.isActive) {
      setVotingFor(null);
    }
  }, [votingState?.isActive]);

  const handleVote = async (sheetId: string) => {
    if (!votingState?.voteId || hasVoted || votingFor) return;

    setVotingFor(sheetId);

    try {
      const audienceId = identity.audienceId;
      if (!audienceId) {
        alert('Errore: ID audience non trovato. Ricarica la pagina.');
        setVotingFor(null);
        return;
      }

      const response = await fetch(`/api/sheet-votes/${votingState.voteId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audienceId,
          optionType: 'sheet',
          sheetId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore durante il voto');
      }

      // Notifica il parent che il voto è stato completato
      onVoteComplete?.(sheetId);
    } catch (err: any) {
      alert(`Errore: ${err.message}`);
      setVotingFor(null);
    }
  };

  const loadSheets = async () => {
    try {
      const sessionId = identity.sessionId;

      if (!sessionId) {
        setLoading(false);
        return;
      }

      // Get sheet assignments for the session
      const response = await fetch(`/api/sheets/session/${sessionId}/assignments`);
      if (response.ok) {
        const data = await response.json();
        const teamKey = userTeam.toLowerCase();

        // Get primary sheets for user's team
        const teamSheets = data.groupedByTeam?.[teamKey]?.primary || [];

        // Extract sheet info
        const sheetInfos: SheetInfo[] = teamSheets.map((assignment: any) => ({
          id: assignment.sheet.id,
          name: assignment.sheet.name,
          imageUrl: assignment.sheet.imageUrl,
        }));

        setSheets(sheetInfos);
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to load sheets:', err);
      setLoading(false);
    }
  };

  const teamBtnClass = getTeamButtonClass(userTeam);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-10">
        <NicknameBar />
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400"></div>
      </div>
    );
  }

  // Show waiting screen when it's another team's turn in SEQUENTIAL mode
  if (waitingForTeam) {
    const teamName = TEAM_SHORT_NAMES[waitingForTeam] || waitingForTeam;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-10">
        <NicknameBar />
        <div className="text-center">
          <div className="text-6xl mb-6 animate-pulse">&#9719;</div>
          <h2 className="text-3xl font-heading font-bold text-ink mb-4">
            Attendere...
          </h2>
          <p className="text-ink-light text-xl mb-2 font-body">
            Votazione della squadra{' '}
            <span className="font-bold text-team-yellow">
              {teamName}
            </span>{' '}
            in corso
          </p>
          <p className="text-ink-light text-base mt-6 font-body">
            Sara' il vostro turno tra poco!
          </p>
          {/* Team Badge */}
          <div className="mt-8">
            <TeamBadge teamColor={userTeam} size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-4 pt-10 overflow-hidden">
      <NicknameBar />
      {/* Header compatto */}
      <div className="text-center mb-3 flex-shrink-0">
        <img src="/images/dw_logo.png" alt="Dungeon World - The Show" className="mx-auto w-40 mb-2" />
        {/* Team Badge */}
        <TeamBadge teamColor={userTeam} size="sm" />
        {hasVoted ? (
          <div className="mt-2">
            <p className="text-team-green font-heading font-semibold">
              &#10003; Voto inviato!
            </p>
          </div>
        ) : isVotingActive ? (
          <p className="text-team-yellow font-heading font-semibold animate-pulse mt-2">
            Vota per il tuo eroe!
          </p>
        ) : waitingForResults ? (
          <p className="text-ink-light mt-2 font-body">
            In attesa dei risultati...
          </p>
        ) : (
          <p className="text-ink-light text-base mt-2 font-body">
            Tocca un personaggio per vedere la sua scheda
          </p>
        )}
      </div>

      <OrnamentSeparator className="my-2" />

      {/* Lista eroi - flex grow per occupare lo spazio rimanente */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {waitingForResults ? null : sheets.length === 0 ? (
          <div className="text-ink-light text-center py-8 font-body">
            <p className="text-xl mb-2">Nessun personaggio ancora</p>
            <p className="text-base">Lo spettacolo non ha ancora iniziato la selezione dei personaggi</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sheets.map((sheet) => (
              <div
                key={sheet.id}
                className="parchment-card p-3"
              >
                {/* Riga nome con immagine */}
                <div className="flex items-center gap-3">
                  {sheet.imageUrl && (
                    <img
                      src={sheet.imageUrl}
                      alt={sheet.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-parchment-300 flex-shrink-0"
                    />
                  )}
                  <div className="text-ink font-heading font-bold text-xl">{sheet.name}</div>
                </div>

                {/* Pulsanti azione a capo */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => onViewCharacter?.(sheet.id)}
                    className="flex-1 bg-parchment-300 hover:bg-parchment-400 text-ink px-3 py-2 rounded-lg text-base font-heading font-bold transition"
                  >
                    Vedi
                  </button>

                  {isVotingActive && !hasVoted && (
                    <button
                      onClick={() => handleVote(sheet.id)}
                      disabled={votingFor !== null}
                      className={`flex-1 px-4 py-2 font-bold rounded-lg transition text-base ${
                        votingFor === sheet.id
                          ? `${teamBtnClass} opacity-70`
                          : votingFor !== null
                          ? 'bg-parchment-300 text-ink-muted cursor-not-allowed'
                          : teamBtnClass
                      }`}
                    >
                      {votingFor === sheet.id ? '...' : 'Vota'}
                    </button>
                  )}

                  {hasVoted && votedForSheetId === sheet.id && (
                    <div className="flex-1 bg-team-green text-parchment-50 px-4 py-2 font-bold rounded-lg text-base text-center font-heading">
                      &#10003;
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status indicator - fisso in basso */}
      <div className="mt-4 text-ink-light text-sm text-center flex-shrink-0 font-body">
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-team-green-accent rounded-full animate-pulse"></div>
          <span>Connesso</span>
        </div>
      </div>
    </div>
  );
}

export default StandbyScreen;
